import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { AlertTriangle, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Warns master admins when monthly AI spend passes 80% of the cap so
// the budget pause never comes as a surprise (Darren, Jun 2026).
export const BudgetAlertBanner = () => {
  const { user, token } = useAuth();
  const [status, setStatus] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!token || user?.role !== 'master_admin') return;
    axios.get(`${API}/admin/llm-provider/budget`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setStatus(r.data))
      .catch(() => {});
  }, [token, user?.role]);

  if (user?.role !== 'master_admin' || !status?.monthly_cap_usd) return null;
  const pct = status.pct_used || 0;
  if (pct < 80 || dismissed) return null;

  const dismissKey = `ai-budget-banner-${status.month}-${status.paused ? 'paused' : '80'}`;
  if (sessionStorage.getItem(dismissKey)) return null;
  const onDismiss = () => { sessionStorage.setItem(dismissKey, '1'); setDismissed(true); };

  const paused = status.paused;
  return (
    <div
      className={`flex items-center gap-3 px-6 py-2.5 text-sm border-b ${paused ? 'bg-red-50 border-red-200 text-red-800' : 'bg-amber-50 border-amber-200 text-amber-800'}`}
      data-testid="ai-budget-banner"
    >
      <AlertTriangle className={`w-4 h-4 shrink-0 ${paused ? 'text-red-500' : 'text-amber-500'}`} />
      <span className="flex-1">
        {paused ? (
          <><strong>AI budget cap reached</strong> — ${status.month_spend_usd.toFixed(0)} of ${status.monthly_cap_usd.toFixed(0)} used ({pct}%). Assistants, quizzes and insights are paused; grading, playbooks and live chat keep running.</>
        ) : (
          <><strong>AI spend at {pct}%</strong> of the ${status.monthly_cap_usd.toFixed(0)} monthly cap (${status.month_spend_usd.toFixed(0)} used). Non-critical AI pauses at 100%.</>
        )}
      </span>
      <Link
        to="/admin/llm-provider"
        className={`shrink-0 text-xs font-semibold underline underline-offset-2 ${paused ? 'text-red-700' : 'text-amber-700'}`}
        data-testid="ai-budget-banner-link"
      >
        Manage cap
      </Link>
      <button onClick={onDismiss} className="shrink-0 opacity-60 hover:opacity-100" aria-label="Dismiss" data-testid="ai-budget-banner-dismiss">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};
